import { keepPreviousData } from '@tanstack/react-query'
import _ from 'lodash'
import { useMemo, useState } from 'react'
import { BsExclamationTriangleFill, BsFillTelephoneFill } from 'react-icons/bs'
import { MdHome, MdMail, MdPhone, MdPhoneDisabled, MdSmartphone } from 'react-icons/md'
import { RiFolderTransferFill } from 'react-icons/ri'
import { Link } from 'react-router-dom'
import statuses from 'shared_src/dictionaries/episodeOfCare/statuses'

import { useAppSelector } from '../../../store'
import { useAdvancedSearchHook } from '../../advanced-search/advanced-search-hook'
import * as Table from '../../claim-history/simple-table'
import * as Card from '../../shared/card'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import FriendlyDate from '../../shared/Date/FriendlyDate'
import FormattedPhone from '../../shared/Phone/FormattedPhone'

import './styles.scss'

export function PatientList ({ title }) {
  const [ state, setState ] = useState({
    pagination: {
      page: 0,
      count: 25
    },
    sort: {
      by: 'lastName',
      dir: 'asc'
    }
  })

  const [ query ] = useAdvancedSearchHook({
    open: true,
    status: 'all',
    sortBy: state.sort.by,
    sortDir: state.sort.dir,
    count: state.pagination.count,
    page: state.pagination.page
  }, {
    placeholderData: keepPreviousData,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false
  })

  const allClients = useAppSelector((redux) => redux?.globalVars?.clients ?? [])

  const columns = useMemo(() => [
    {
      name: 'lastName',
      header: 'Last',
      getValue: (item) => {
        const client = (item._clientName || item.clientName)
        return (
          <Link to={`/patient/${client}/${item.id}/patientFolder`}>{item.lastName}</Link>
        )
      },
      isSortable: true
    }, {
      name: 'firstName',
      header: 'First',
      isSortable: true
    }, {
      name: 'birthDate',
      header: 'DOB',
      isSortable: true,
      getValue: (item) => <FriendlyDate date={item.birthDate || ''} />
    }, {
      name: 'diagnosis',
      header: 'Program',
      isSortable: true,
      getValue: (item) => item.diagnosis ?? ''
    }, {
      name: 'accuity',
      header: 'PAR',
      gridArea: '0.4fr',
      isSortable: true,
      getValue: (item) => item.accuity ?? ''
    }, {
      name: 'ssn',
      header: 'SSN',
      gridArea: '1.2fr',
      isSortable: true,
      getValue: (item) => item.ssn ?? ''
    },
    {
      name: 'phones',
      isSortable: true,
      header: 'Phone',
      getValue: (item) => (
        item.phone || []).map((p) =>
        (
          <FormattedPhone
            key={p}
            number={p}
          />
        ))
    },
    {
      name: 'phone',
      gridArea: '0.5fr',
      header: (
        <div
          title='Lexis-Nexis phones on the chart'
          key='phone-missed-icon'
        >
          <MdPhone
            fill='#8A8A8A'
            size='1.1rem'
          />
        </div>
      ),
      getValue: (item) => {
        const { _clientName, clientName, id, other, openEncounterStatus } = item
        const client = (_clientName || clientName)

        function Indicator () {
          return (
            _.isEmpty(other)
              ? (
                <div
                  title='No Lexis-Nexis numbers on the chart'
                >
                  <MdPhoneDisabled
                    fill='#F2545A'
                    size='1.1rem'
                  />
                </div>
              )
              : (
                <div
                  title='Lexis-Nexis numbers on the chart'

                >
                  <MdPhone
                    fill='#82B43C'
                    size='1.1rem'
                  />
                </div>
              )
          )
        }

        if (openEncounterStatus) {
          return (
            <Link
              to={`/patient/${client}/${id}/assessment/standarddemographicspatient`}
            >
              <Indicator />
            </Link>
          )
        }
        return <Indicator />
      },
      isSortable: true
    },
    {
      name: 'continueEncounter',
      gridArea: '0.5fr',
      header: (
        <span
          title='Continue open encounter'
          key='play-square-icon'
        >
          <RiFolderTransferFill
            fill='#8A8A8A'
            size='1.1rem'
          />
        </span>
      ),
      getValue: (item) => {
        const client = (item._clientName || item.clientName)

        if (!item.openEncounterStatus) {
          return null
        }

        return (
          <Link to={`/patient/${client}/${item.id}/assessment/igao`}>
            <div title='Continue open encounter'>
              <RiFolderTransferFill
                fill='#967DD9'
                size='1.1rem'
              />
            </div>
          </Link>
        )
      }
    },
    {
      name: 'urgent',
      isSortable: true,
      gridArea: '0.5fr',
      header: (
        <span
          title='Urgent actions on the chart'
          key='alert-triangle-icon'
        >
          <BsExclamationTriangleFill
            fill='#8A8A8A'
            size='1.1rem'
          />
        </span>
      ),
      getValue: (item) => {
        if (!item.urgent) {
          return ''
        }
        return (
          <div title='Urgent actions on the chart'>
            <BsExclamationTriangleFill
              fill='#F2545A'
              size='1.1rem'
            />
          </div>
        )
      }
    },
    {
      name: 'lastEncounter',
      header: 'Last Encounter',
      gridArea: '1.2fr',
      getValue: (item) => (
        item.lastEncounter
          ? (
            <FriendlyDateTime
              full
              datetime={item.lastEncounter}
            />
          )
          : ''
      ),
      isSortable: true
    },
    {
      name: 'lastType',
      isSortable: true,
      header: 'Type',
      getValue: (item) => {
        if (item.lastType === 'home') {
          return (
            <div title='Home Encounter'>
              <MdHome fill='#82B43C' />
            </div>
          )
        }
        if (item.lastType === 'phone') {
          return (
            <div title='Telephonic Encounter'>
              <BsFillTelephoneFill fill='#82B43C' />
            </div>
          )
        }

        if (item.lastType === 'email') {
          return (
            <div title='Email Encounter'>
              <MdMail fill='#82B43C' />
            </div>
          )
        }
        if (item.lastType === 'mobile') {
          return (
            <div title='Mobile Encounter'>
              <MdSmartphone fill='#82B43C' />
            </div>
          )
        }

        return ''
      }
    },
    {
      name: 'status',
      header: 'Status',
      getValue: (item) => item.status
        ? _.find(statuses, x => x.value === item.status).label
        : 'Unknown',
      isSortable: true
    },
    {
      name: 'clientName',
      isSortable: true,
      header: 'Client',
      getValue: (item) => {
        const clientNames = _.reduce(
          allClients,
          (acc, c) => {
            return { ...acc, [c.clientName]: c.display }
          },
          {}
        )

        const client = item?._clientName || item?.clientName
        return clientNames[client] || client
      }
    }
  ], [ allClients ])

  return (
    <Card.Card>
      <Card.Header><h2>{title}</h2></Card.Header>
      <Table.Table
        expandable={false}
        data={ query.data?.data ?? []}
        error={query.error}
        fetchStatus={query.fetchStatus}
        queryStatus='success'
        noDataPlaceholder='There are no open encounters'
        sort={{ field: state.sort.by, direction: state.sort.dir }}
        onSortChange={(field, dir) => {
          setState((prev) => ({
            ...prev,
            sort: {
              dir,
              by: field
            }
          }))
        }}
        columns={columns}
        pagination={{ page: state.pagination.page, pageSize: state.pagination.count, totalFiltered: query?.data?.total }}
        onPageSizeChange={(size) => {
          setState((prev) => ({
            ...prev,
            pagination: {
              count: size ?? 0,
              page: 0
            }
          }))
        }}
        onPageChange={(page) => {
          setState((prev) => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              page: page - 1
            }
          }))
        }}
        disableMinHeight
      />
    </Card.Card>
  )
}
